import { useState, useEffect } from 'react';

import { Grid, Link, TextField, Typography } from '@mui/material';
import { startOfDay } from 'date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';

import AlarmLogTable from './AlarmLogTable';
import { ModuleType } from '../analyzerModules/types';
import { SnapshotAlarmLogReturnWithStop } from './types';
import { joinAlarmStopsToStarts } from './utils/logManipulation';

import { useSnapshots } from 'features/snapshots/api/getSnapshots';
import { useAlarmLog } from './api/getAlarmLog';
import AnalyzerSelectLocal from 'features/analyzers/AnalyzerSelectLocal';
import { Controller, useForm } from 'react-hook-form';
import SnapshotSelectLocal from 'features/snapshots/SnapshotSelectLocal';
import { useGetAnalyzersByModule } from 'features/analyzers/hooks/useGetAnalyzersByModule';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { zonedTimeToUtc } from 'date-fns-tz';

export type AlarmLogForm = {
  analyzerId: number;
  snapshotId: number;
  fromDate: Date;
  toDate: Date;
};

const AlarmLogPage = () => {
  const analyzersForModule = useGetAnalyzersByModule(ModuleType.BASE);
  const { dateFormatWithZeros, timezone, timeFormat, ampm } = useUserDateTime();
  const { control, watch } = useForm<AlarmLogForm>({
    defaultValues: {
      analyzerId: -1,
      snapshotId: -1,
      fromDate: startOfDay(new Date()),
      toDate: new Date(),
    },
  });

  const { data: snapshots } = useSnapshots({ analyzerId: watch('analyzerId') });

  const { data: alarmLog } = useAlarmLog({
    analyzerId: watch('analyzerId'),
    snapshotId: watch('snapshotId'),
    fromDate: zonedTimeToUtc(watch('fromDate'), timezone).toISOString(),
    toDate: zonedTimeToUtc(watch('toDate'), timezone).toISOString(),
  });

  const [formattedAlarmLog, setFormattedAlarmLog] = useState<
    SnapshotAlarmLogReturnWithStop[]
  >([]);

  useEffect(() => {
    if (alarmLog) {
      const responseWithJoinedStops = joinAlarmStopsToStarts(alarmLog);
      setFormattedAlarmLog(responseWithJoinedStops);
    }
  }, [alarmLog]);

  if (analyzersForModule?.length === 0) {
    return (
      <Grid container>
        <Typography>
          To view alarm logs, please add analyzers with purchased BASE or GEO
          modules. Contact{' '}
          <Link href='mailto:sales@lptech.com'>LP Technologies</Link> for more
          information.
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid container item xs={12} spacing={2}>
        <Grid item style={{ width: '15rem' }}>
          <Controller
            name='fromDate'
            control={control}
            render={({ field: { onChange, value } }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label='From'
                  inputFormat={`${dateFormatWithZeros} ${timeFormat}`}
                  value={value}
                  onChange={onChange}
                  renderInput={(params: any) => <TextField {...params} />}
                  ampm={ampm}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item style={{ width: '15rem' }}>
          <Controller
            name='toDate'
            control={control}
            render={({ field: { onChange, value } }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label='To'
                  inputFormat={`${dateFormatWithZeros} ${timeFormat}`}
                  value={value}
                  onChange={onChange}
                  renderInput={(params: any) => <TextField {...params} />}
                  ampm={ampm}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item style={{ minWidth: '12rem' }}>
          <Controller
            name='analyzerId'
            control={control}
            rules={{
              required: false,
            }}
            render={({ field: { onChange, value } }) => (
              <AnalyzerSelectLocal
                analyzers={analyzersForModule}
                selectedAnalyzerId={value}
                handleAnalyzerChanged={onChange}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item style={{ minWidth: '12rem' }}>
          <Controller
            name='snapshotId'
            control={control}
            rules={{
              required: false,
            }}
            render={({ field: { onChange, value } }) => (
              <SnapshotSelectLocal
                snapshots={snapshots}
                selectedSnapshotId={value}
                handleSnapshotChanged={onChange}
                fullWidth
              />
            )}
          />
        </Grid>
      </Grid>
      {watch('fromDate') > watch('toDate') ? (
        <Grid item xs={12}>
          <h3 style={{ color: 'red' }}>To Date is before From Date</h3>
        </Grid>
      ) : (
        <Grid item xs={12}>
          <AlarmLogTable alarmLog={formattedAlarmLog || []} />
        </Grid>
      )}
    </Grid>
  );
};

export default AlarmLogPage;
